import { IconWrapper } from '@flexo/general';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';
import { FooterProps } from '../../organisms.interfaces';
import styles from './footer.module.scss';

import { Keyboard } from '@capacitor/keyboard';
import { METADATA } from '@flexo/reducers';
import { navigateToCommunity, navigateToSite } from '@flexo/resolvers';
import { useDispatch, useSelector } from 'react-redux';
const ENV = (import.meta as any).env;

export function Footer({ sections, shadow, roundCorners }: FooterProps) {
  const { Footer__Label, Footer__Label__Active } = styles;
  const { t } = useTranslation();

  const params = useParams();
  const location = useLocation();
  const pathSegment = location.pathname.split('/')[1];
  const dispatch = useDispatch();

  const metadata = useSelector((state: any) => state.metadata);

  const activeModule = pathSegment || 'household';

  let isActiveSection = activeModule ? activeModule : null;

  function getCommunitySite() {
    const sites = (metadata?.entities as any)?.site || [];
    const prevSiteId = metadata?.prevSelectedEntityId as string | null;

    // Determine current community in context:
    // - If on community pages → use route param
    // - If on site pages → resolve community from current site
    // - Else → try selected entity community, then fallback to first community of sites
    const isCommunityContext =
      pathSegment === 'community' || pathSegment?.startsWith('community-');
    const isSiteContext =
      pathSegment === 'site' ||
      pathSegment === 'household' ||
      pathSegment === 'site-energy' ||
      pathSegment === 'site-economics';

    let currentCommunityId: string | null = null;
    if (isCommunityContext) {
      currentCommunityId = (params?.id as string) || null;
    } else if (isSiteContext) {
      const currentSite = sites.find(
        (s: any) => s?.siteId === (params?.id as string)
      );
      currentCommunityId = currentSite?.communityId || null;
    } else {
      currentCommunityId =
        (metadata?.selectedEntity as any)?.communityId || null;
      if (!currentCommunityId) {
        currentCommunityId = sites?.[0]?.communityId || null;
      }
    }

    // Prioritize prevSelectedEntityId if it exists and is valid
    if (prevSiteId && sites.find((s: any) => s?.siteId === prevSiteId)) {
      return prevSiteId;
    }

    // Otherwise, pick the first site in the current community
    const firstSiteInCommunity = sites.find(
      (s: any) => s?.communityId === currentCommunityId
    );
    if (firstSiteInCommunity) {
      return firstSiteInCommunity.siteId;
    }

    // Final fallback: first site overall
    return sites?.[0]?.siteId || null;
  }

  // Handle section change
  const handleSectionChange = (section: string) => {
    if (section === 'community') {
      const sites = (metadata?.entities as any)?.site || [];

      // Capture current siteId (only if we are in a site context)
      const isSiteContext =
        pathSegment === 'site' || pathSegment === 'household';
      const currentSiteId = isSiteContext
        ? (params?.id as string) || null
        : (metadata?.selectedEntity as any)?.siteId || null;
      if (currentSiteId) {
        dispatch(METADATA.SetPrevSelectedEntityId(currentSiteId as any));
      }

      // Determine target community:
      // - If we have a current site → navigate to its community
      // - Else if already on community → keep same community
      // - Else fallback to selected entity community or first available community from sites
      let targetCommunityId: string | null = null;
      if (currentSiteId) {
        const currentSite = sites.find((s: any) => s?.siteId === currentSiteId);
        targetCommunityId = currentSite?.communityId || null;
      }
      if (!targetCommunityId) {
        if (
          pathSegment === 'community' ||
          pathSegment?.startsWith('community-')
        ) {
          targetCommunityId = (params?.id as string) || null;
        } else {
          targetCommunityId =
            (metadata?.selectedEntity as any)?.communityId ||
            sites?.[0]?.communityId ||
            (metadata?.entities as any)?.community?.[0]?.communityId ||
            null;
        }
      }

      navigateToCommunity('community', targetCommunityId as string);
    } else {
      navigateToSite('household', getCommunitySite());
      isActiveSection = section;
    }
  };
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);

  useEffect(() => {
    Keyboard.addListener('keyboardWillShow', () => setIsKeyboardOpen(true));
    Keyboard.addListener('keyboardWillHide', () => setIsKeyboardOpen(false));

    return () => {
      try {
        Keyboard.removeAllListeners();
      } catch (error) {
        console.log(error);
      }
    };
  }, []);

  return (
    <div
      className={`${styles.Footer} sfPro ${shadow ? styles.shadow : ''}
      ${roundCorners ? styles.roundCorners : ''}`}
      style={
        isKeyboardOpen
          ? { display: 'none' }
          : ENV?.VITE_APP_NAME !== 'plenitude'
          ? { alignItems: 'center' }
          : {}
      }
    >
      {/* Map through sections and display icons */}
      {sections?.map((section) => (
        <div
          key={section}
          className={`${Footer__Label} ${
            isActiveSection === section ? Footer__Label__Active : ''
          }`}
          onClick={() => handleSectionChange(section)}
        >
          <IconWrapper
            iconName={`${section}${
              isActiveSection === section ? '_active' : ''
            }`}
          />
          <div
            className={isActiveSection === section ? 'color-primary-500' : ''}
            style={{ marginTop: '-4px', textTransform: 'capitalize' }}
          >
            {t(`widgets.footer.sections.${section}`)}
          </div>
        </div>
      ))}
    </div>
  );
}

export default Footer;
